import { useState } from 'react';
import {
  useAssets,
  useAssetSummary,
  useDeleteAsset,
  useAssetCategories,
} from '../../../services/httpServices/queries';
import { useDebounce } from '../../../hooks/useDebounce';
import { Pagination } from '../../../components/ui/Pagination';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { FixedAssetsLayout } from './FixedAssetsLayout';
import { AssetFormModal } from './AssetFormModal';
import {
  PlusCircle,
  Loader2,
  AlertCircle,
  Search,
  Trash2,
  Pencil,
  Boxes,
  Wallet,
  ShieldCheck,
} from 'lucide-react';
import type { FixedAsset } from '../../../types/asset';
import { STATUS_LABELS, fmtMoney } from './assetLabels';

const limit = 20;

function StatusBadge({ status }: { status: string }) {
  const classes: Record<string, string> = {
    ACTIVE: 'bg-success/10 text-success',
    UNDER_MAINTENANCE: 'bg-warning/10 text-warning',
    DISPOSED: 'bg-danger/10 text-danger',
    RETIRED: 'bg-background text-text-muted',
  };
  return (
    <span
      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${classes[status] || 'bg-background text-text-muted'}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function categoryName(asset: FixedAsset): string {
  return asset.assetCategory?.name || '—';
}

export function AssetRegister() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [categoryId, setCategoryId] = useState('');
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FixedAsset | null>(null);
  const [confirmAsset, setConfirmAsset] = useState<FixedAsset | null>(null);

  const { data, isLoading, error } = useAssets({
    search: debouncedSearch || undefined,
    categoryId: categoryId || undefined,
    page,
    limit,
  });
  const { data: summaryData } = useAssetSummary({
    search: debouncedSearch || undefined,
    categoryId: categoryId || undefined,
  });
  const { data: categoriesData } = useAssetCategories(undefined, true, 'ASSET');
  const categories = categoriesData?.data || [];
  const deleteAsset = useDeleteAsset();

  const assets = data?.data || [];
  const meta = data?.meta;
  const summary = summaryData?.data;

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (asset: FixedAsset) => {
    setEditing(asset);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const handleDelete = async () => {
    if (!confirmAsset) return;
    try {
      await deleteAsset.mutateAsync(confirmAsset.id);
    } finally {
      setConfirmAsset(null);
    }
  };

  const summaryCards = [
    { label: 'Total Assets', value: String(summary?.count ?? 0), icon: Boxes, color: 'bg-primary/10 text-primary' },
    { label: 'Register Value', value: fmtMoney(summary?.totalValue), icon: Wallet, color: 'bg-success/10 text-success' },
    { label: 'Active', value: String(summary?.activeCount ?? 0), icon: ShieldCheck, color: 'bg-primary/10 text-primary' },
  ];

  return (
    <FixedAssetsLayout
      title="Assets"
      description="The fixed assets we own"
      helpKey="/billing/assets"
      action={
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <PlusCircle size={16} />
          Add Asset
        </button>
      }
    >
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-surface rounded-xl border border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-muted uppercase">{card.label}</p>
                <p className="text-xl font-bold text-text mt-1">{card.value}</p>
              </div>
              <div className={`p-2.5 rounded-lg ${card.color}`}>
                <card.icon size={18} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name or code..."
            className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <select
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 sm:w-56"
        >
          <option value="">All categories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load assets</span>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Asset</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Category</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Purchased</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {assets.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                      {search || categoryId ? (
                        <span>
                          No assets match your filters.{' '}
                          <button
                            onClick={() => {
                              setSearch('');
                              setCategoryId('');
                            }}
                            className="text-primary hover:underline"
                          >
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No assets yet. Add your first asset to get started.'
                      )}
                    </td>
                  </tr>
                ) : (
                  assets.map((asset) => (
                    <tr key={asset.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3 text-sm font-mono text-primary">{asset.assetCode}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-text">{asset.name}</p>
                        {(asset.supplierName || asset.location) && (
                          <p className="text-xs text-text-muted">
                            {[asset.supplierName || asset.vendor?.name, asset.location]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">{categoryName(asset)}</td>
                      <td className="px-4 py-3 text-sm text-text text-right">{asset.quantity}</td>
                      <td className="px-4 py-3 text-sm font-medium text-text text-right">{fmtMoney(asset.totalCost)}</td>
                      <td className="px-4 py-3 text-sm text-text">
                        {new Date(asset.purchaseDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={asset.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(asset)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => setConfirmAsset(asset)}
                            disabled={deleteAsset.isPending}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {assets.length > 0 && (
            <Pagination
              page={page}
              totalPages={meta?.totalPages || 1}
              total={meta?.total || 0}
              limit={limit}
              onPageChange={setPage}
              itemName="assets"
            />
          )}
        </div>
      )}

      <AssetFormModal open={formOpen} onClose={closeForm} asset={editing} />

      <ConfirmDialog
        open={confirmAsset !== null}
        title="Delete asset"
        description={
          <>
            Delete <strong>{confirmAsset?.name}</strong>? This removes it from the asset list.
          </>
        }
        loading={deleteAsset.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmAsset(null)}
      />
    </FixedAssetsLayout>
  );
}
